import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2 } from 'lucide-react';

export function QuestionModal({ open, onClose, onSave, question, topicId }) {
  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    options: [{ text: '', isCorrect: false }],
    explanation: '',
    topic: topicId,
  });

  useEffect(() => {
    if (question) {
      setFormData({
        title: question.title || '',
        imageUrl: question.imageUrl || '',
        options: question.options?.length > 0 
          ? question.options 
          : [{ text: '', isCorrect: false }],
        explanation: question.explanation || '',
        topic: question.topic || topicId,
      });
    } else {
      setFormData({
        title: '',
        imageUrl: '',
        options: [{ text: '', isCorrect: false }],
        explanation: '',
        topic: topicId,
      });
    }
  }, [question, topicId, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, topic: topicId });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { text: '', isCorrect: false }],
    });
  };

  const removeOption = (index) => {
    if (formData.options.length > 1) {
      setFormData({
        ...formData,
        options: formData.options.filter((_, i) => i !== index),
      });
    }
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="question-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {question ? 'Soruyu Düzenle' : 'Yeni Soru Ekle'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="question-title">Soru Metni</Label>
            <Textarea
              id="question-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Soruyu yazın..."
              required
              rows={3}
              data-testid="question-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="question-image">Görsel URL (Opsiyonel)</Label>
            <Input
              id="question-image"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              placeholder="https://..."
              data-testid="question-image-input"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Seçenekler</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addOption}
                className="text-teal-700"
                data-testid="add-option-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Seçenek Ekle
              </Button>
            </div>
            {formData.options.map((option, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={option.isCorrect}
                  onCheckedChange={(checked) => updateOption(index, 'isCorrect', checked)}
                  data-testid={`option-correct-${index}`}
                />
                <Input
                  value={option.text}
                  onChange={(e) => updateOption(index, 'text', e.target.value)}
                  placeholder={`Seçenek ${String.fromCharCode(65 + index)}`}
                  className="flex-1"
                  required
                  data-testid={`option-text-${index}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeOption(index)}
                  disabled={formData.options.length <= 1}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`remove-option-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <p className="text-xs text-slate-500">Doğru cevabı işaretleyin</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="question-explanation">Açıklama (Opsiyonel)</Label>
            <Textarea
              id="question-explanation"
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              placeholder="Cevabın açıklaması..."
              rows={2}
              data-testid="question-explanation-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              İptal
            </Button>
            <Button 
              type="submit" 
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="question-save-btn"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
